"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import {
  clearSportsbookOverride,
  loadSportsbookPreferences,
  resetComparisonOrder as resetComparisonOrderPreference,
  saveChartBooksPreference,
  saveComparisonOrder as saveComparisonOrderPreference,
  saveGlobalSportsbookProfile,
  saveMultiSportsbookOverride,
  saveSingleSportsbookOverride,
  type SaveGlobalSportsbookProfileInput,
  type SportsbookPreferenceActionResult,
} from "@/lib/actions/sportsbook-preferences";
import {
  createMultiSportsbookOverride,
  createSingleSportsbookOverride,
  getEffectiveMultiSportsbookIds,
  getGlobalDefaultSportsbookId,
  getGlobalSportsbookIds,
  getOptimizerComparisonOrderState,
  getSportsbookSurfacePolicy,
  isEmptyMultiSportsbookOverride,
  type ChartBooksPreference,
  type GlobalSportsbookProfile,
  type OptimizerComparisonOrderPreference,
  type SportsbookPreferencesBootstrap,
  type SportsbookPreferencesState,
  type SportsbookPreferencesStatus,
  type SportsbookSurfaceId,
  type ToolSportsbookOverride,
} from "@/lib/sportsbook-preferences";
import type { SportsbookId } from "@/lib/sportsbook-registry";

const BROADCAST_CHANNEL_PREFIX = "upsider:sportsbook-preferences";
const BROADCAST_MESSAGE = "preferences-changed";
const TOOL_OVERRIDE_BROADCAST_MESSAGE = "tool-override-changed";
const NOT_READY_ERROR = "Sportsbook preferences are not ready. Retry first.";
const ACCOUNT_CHANGED_ERROR = "The active account changed before the preference save completed.";
const SAVE_TRANSPORT_ERROR = "Unable to save sportsbook preferences. Please try again.";

export type ToolSaveResult =
  | { readonly success: true }
  | { readonly success: false; readonly error: string };

export interface SportsbookPreferencesActions {
  saveProfile(
    input: SaveGlobalSportsbookProfileInput,
  ): Promise<SportsbookPreferenceActionResult<GlobalSportsbookProfile>>;
  saveMultiSelection(
    surface: SportsbookSurfaceId,
    selectedBookIds: readonly string[],
  ): Promise<ToolSaveResult>;
  saveSingleSelection(
    surface: SportsbookSurfaceId,
    bookId: string,
  ): Promise<ToolSaveResult>;
  clearOverride(surface: SportsbookSurfaceId): Promise<ToolSaveResult>;
  saveChartBooks(
    preference: ChartBooksPreference,
  ): Promise<SportsbookPreferenceActionResult<ChartBooksPreference>>;
  saveComparisonOrder(
    bookIds: readonly string[],
    hiddenIds: readonly string[],
  ): Promise<SportsbookPreferenceActionResult<OptimizerComparisonOrderPreference>>;
  resetComparisonOrder(): Promise<SportsbookPreferenceActionResult<{ reset: true }>>;
  retry(): Promise<boolean>;
}

export interface SportsbookPreferencesMeta {
  status: SportsbookPreferencesStatus;
  isSaving: boolean;
  error: string | null;
  isConfigured: boolean;
  globalBookIds: readonly SportsbookId[];
  globalDefaultBookId: SportsbookId;
  getEffectiveBookIds(surface: SportsbookSurfaceId): SportsbookId[];
  isOverrideActive(surface: SportsbookSurfaceId): boolean;
}

export interface SportsbookPreferencesContextValue {
  state: SportsbookPreferencesState;
  actions: SportsbookPreferencesActions;
  meta: SportsbookPreferencesMeta;
}

const SportsbookPreferencesStateContext = createContext<SportsbookPreferencesState | null>(null);
const SportsbookPreferencesActionsContext = createContext<SportsbookPreferencesActions | null>(null);
const SportsbookPreferencesMetaContext = createContext<SportsbookPreferencesMeta | null>(null);

type SurfaceWrite =
  | { readonly kind: "put"; readonly override: ToolSportsbookOverride }
  | { readonly kind: "delete" };

const DELETE_SURFACE_OVERRIDE: SurfaceWrite = Object.freeze({ kind: "delete" });

function toSurfaceWrite(override: ToolSportsbookOverride): SurfaceWrite {
  return override.kind === "multi" && isEmptyMultiSportsbookOverride(override)
    ? DELETE_SURFACE_OVERRIDE
    : { kind: "put", override };
}

function applySurfaceWrite(
  state: SportsbookPreferencesState,
  surface: SportsbookSurfaceId,
  write: SurfaceWrite,
): SportsbookPreferencesState {
  const overrides = { ...state.overrides };
  if (write.kind === "put") overrides[surface] = write.override;
  else delete overrides[surface];
  return { ...state, overrides };
}

type PendingWrites = Readonly<Partial<Record<SportsbookSurfaceId, SurfaceWrite>>>;

interface ToolSave {
  readonly write: SurfaceWrite;
  readonly send: () => Promise<SportsbookPreferenceActionResult<SurfaceWrite>>;
}

interface SurfaceChain {
  next: ToolSave | null;
  lastSaved: SurfaceWrite | null;
  readonly waiters: Array<(result: ToolSaveResult) => void>;
}

const NO_PENDING_WRITES: PendingWrites = Object.freeze({});

function withPendingWrites(
  state: SportsbookPreferencesState,
  pending: PendingWrites,
): SportsbookPreferencesState {
  return (Object.entries(pending) as [SportsbookSurfaceId, SurfaceWrite][]).reduce(
    (next, [surface, write]) => applySurfaceWrite(next, surface, write),
    state,
  );
}

function mapSaved<T>(
  result: SportsbookPreferenceActionResult<T>,
  toWrite: (data: T) => SurfaceWrite,
): SportsbookPreferenceActionResult<SurfaceWrite> {
  return result.success ? { success: true, data: toWrite(result.data) } : result;
}

const UNAVAILABLE_STATE: SportsbookPreferencesState = Object.freeze({
  profile: null,
  overrides: Object.freeze({}),
  comparisonOrder: Object.freeze({ kind: "missing" as const }),
});
const UNAVAILABLE_ERROR = "Sportsbook preferences are unavailable outside the authenticated app shell.";
const unavailableResult = <T,>(): SportsbookPreferenceActionResult<T> => ({
  success: false,
  error: UNAVAILABLE_ERROR,
});
const UNAVAILABLE_TOOL_SAVE: ToolSaveResult = Object.freeze({ success: false, error: UNAVAILABLE_ERROR });
const UNAVAILABLE_ACTIONS: SportsbookPreferencesActions = Object.freeze({
  saveProfile: async () => unavailableResult<GlobalSportsbookProfile>(),
  saveMultiSelection: async () => UNAVAILABLE_TOOL_SAVE,
  saveSingleSelection: async () => UNAVAILABLE_TOOL_SAVE,
  clearOverride: async () => UNAVAILABLE_TOOL_SAVE,
  saveChartBooks: async () => unavailableResult<ChartBooksPreference>(),
  saveComparisonOrder: async () => unavailableResult<OptimizerComparisonOrderPreference>(),
  resetComparisonOrder: async () => unavailableResult<{ reset: true }>(),
  retry: async () => false,
});
const UNAVAILABLE_META: SportsbookPreferencesMeta = Object.freeze({
  status: "unavailable",
  isSaving: false,
  error: UNAVAILABLE_ERROR,
  isConfigured: false,
  globalBookIds: Object.freeze(getGlobalSportsbookIds(null)),
  globalDefaultBookId: getGlobalDefaultSportsbookId(null),
  getEffectiveBookIds: (surface: SportsbookSurfaceId) => getEffectiveMultiSportsbookIds(null, null, surface),
  isOverrideActive: () => false,
});

export function SportsbookPreferencesProvider({
  children,
  initialState,
}: {
  children: ReactNode;
  initialState: SportsbookPreferencesBootstrap;
}) {
  const { refresh: refreshRoute } = useRouter();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [state, setState] = useState<SportsbookPreferencesState>(() => ({
    profile: initialState.profile,
    overrides: initialState.overrides,
    chartBooks: initialState.chartBooks,
    comparisonOrder: getOptimizerComparisonOrderState(initialState),
  }));
  const [status, setStatus] = useState<SportsbookPreferencesStatus>(initialState.status);
  const statusRef = useRef(status);
  statusRef.current = status;
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingWrites, setPendingWrites] = useState<PendingWrites>(NO_PENDING_WRITES);
  const chainsRef = useRef(new Map<SportsbookSurfaceId, SurfaceChain>());
  const profileRef = useRef(state.profile);
  profileRef.current = state.profile;
  const loadedUserIdRef = useRef<string | null>(initialState.userId);
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const activeUserIdRef = useRef<string | null>(initialState.userId);
  const accountGenerationRef = useRef(0);
  const reloadSequenceRef = useRef(0);

  const resolvedUserId = isAuthLoading ? initialState.userId : (user?.id ?? null);
  if (activeUserIdRef.current !== resolvedUserId) {
    activeUserIdRef.current = resolvedUserId;
    accountGenerationRef.current += 1;
  }

  const applyBootstrap = useCallback((next: SportsbookPreferencesBootstrap) => {
    loadedUserIdRef.current = next.userId;
    setState({
      profile: next.profile,
      overrides: next.overrides,
      chartBooks: next.chartBooks,
      comparisonOrder: getOptimizerComparisonOrderState(next),
    });
    setStatus(next.status);
    setError(next.status === "unavailable" ? "Sportsbook preferences are temporarily unavailable." : null);
  }, []);

  const reload = useCallback(async (
    mode: "blocking" | "background" = "blocking",
  ): Promise<boolean> => {
    const expectedUserId = activeUserIdRef.current;
    const expectedGeneration = accountGenerationRef.current;
    const sequence = ++reloadSequenceRef.current;
    if (mode === "blocking") {
      setStatus("loading");
      setError(null);
    }
    const keepsTrustedState = () =>
      mode === "background" && statusRef.current === "ready" && loadedUserIdRef.current === expectedUserId;
    let next: SportsbookPreferencesBootstrap;
    try {
      next = await loadSportsbookPreferences();
    } catch {
      if (
        sequence !== reloadSequenceRef.current ||
        expectedGeneration !== accountGenerationRef.current ||
        expectedUserId !== activeUserIdRef.current
      ) {
        return false;
      }
      if (keepsTrustedState()) return false;
      if (loadedUserIdRef.current !== expectedUserId) {
        loadedUserIdRef.current = expectedUserId;
        setState({
          profile: null,
          overrides: {},
          comparisonOrder: { kind: "missing" },
        });
      }
      setStatus("unavailable");
      setError("Sportsbook preferences are temporarily unavailable.");
      return false;
    }
    if (
      sequence !== reloadSequenceRef.current ||
      expectedGeneration !== accountGenerationRef.current ||
      expectedUserId !== activeUserIdRef.current
    ) {
      return false;
    }

    if (next.status === "unavailable") {
      if (keepsTrustedState()) return false;
      if (loadedUserIdRef.current !== expectedUserId) {
        loadedUserIdRef.current = expectedUserId;
        setState({
          profile: null,
          overrides: {},
          comparisonOrder: { kind: "missing" },
        });
      }
      setStatus("unavailable");
      setError("Sportsbook preferences are temporarily unavailable.");
      return false;
    }

    if (next.userId !== expectedUserId) {
      loadedUserIdRef.current = expectedUserId;
      setState({
        profile: null,
        overrides: {},
        comparisonOrder: { kind: "missing" },
      });
      setStatus("unavailable");
      setError("The active account could not be verified. Retry before saving.");
      return false;
    }
    applyBootstrap(next);
    return true;
  }, [applyBootstrap]);

  const publishChange = useCallback(() => {
    broadcastRef.current?.postMessage(BROADCAST_MESSAGE);
    refreshRoute();
  }, [refreshRoute]);

  useEffect(() => {
    if (initialState.userId !== activeUserIdRef.current) return;
    setState({
      profile: initialState.profile,
      overrides: initialState.overrides,
      chartBooks: initialState.chartBooks,
      comparisonOrder: getOptimizerComparisonOrderState(initialState),
    });
    setStatus(initialState.status);
    loadedUserIdRef.current = initialState.userId;
  }, [initialState]);

  useEffect(() => {
    broadcastRef.current?.close();
    broadcastRef.current = null;

    if (!resolvedUserId || typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(`${BROADCAST_CHANNEL_PREFIX}:${resolvedUserId}`);
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (event.data === TOOL_OVERRIDE_BROADCAST_MESSAGE) {
        void reload("background");
      } else if (event.data === BROADCAST_MESSAGE) {
        void reload("background").then((success) => {
          if (success) refreshRoute();
        });
      }
    };
    channel.addEventListener("message", handleMessage);
    broadcastRef.current = channel;

    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      if (broadcastRef.current === channel) broadcastRef.current = null;
    };
  }, [reload, resolvedUserId, refreshRoute]);

  useEffect(() => {
    if (isAuthLoading || resolvedUserId === loadedUserIdRef.current) return;
    setIsSaving(false);
    setPendingWrites(NO_PENDING_WRITES);
    chainsRef.current.clear();
    if (!resolvedUserId) {
      loadedUserIdRef.current = null;
      setState({
        profile: null,
        overrides: {},
        comparisonOrder: { kind: "missing" },
      });
      setStatus("ready");
      setError(null);
      return;
    }
    void reload("blocking");
  }, [isAuthLoading, reload, resolvedUserId]);

  const withSaving = useCallback(async <T,>(
    operation: () => Promise<SportsbookPreferenceActionResult<T>>,
    commit: (data: T) => void,
  ): Promise<SportsbookPreferenceActionResult<T>> => {
    if (
      status !== "ready" ||
      loadedUserIdRef.current !== activeUserIdRef.current
    ) {
      return { success: false, error: NOT_READY_ERROR };
    }

    setIsSaving(true);
    setError(null);
    const expectedUserId = activeUserIdRef.current;
    const expectedGeneration = accountGenerationRef.current;
    try {
      const result = await operation();
      if (
        expectedGeneration !== accountGenerationRef.current ||
        expectedUserId !== activeUserIdRef.current
      ) {
        return { success: false, error: ACCOUNT_CHANGED_ERROR };
      }
      if (!result.success) {
        setError(result.error);
        return result;
      }
      reloadSequenceRef.current += 1;
      commit(result.data);
      publishChange();
      return result;
    } catch {
      if (
        expectedGeneration !== accountGenerationRef.current ||
        expectedUserId !== activeUserIdRef.current
      ) {
        return { success: false, error: ACCOUNT_CHANGED_ERROR };
      }
      setError(SAVE_TRANSPORT_ERROR);
      return { success: false, error: SAVE_TRANSPORT_ERROR };
    } finally {
      if (expectedGeneration === accountGenerationRef.current) {
        setIsSaving(false);
      }
    }
  }, [publishChange, status]);

  const drainChain = useCallback(async (
    surface: SportsbookSurfaceId,
    chain: SurfaceChain,
    generation: number,
  ): Promise<void> => {
    let result: ToolSaveResult = { success: true };
    while (chain.next) {
      const save = chain.next;
      chain.next = null;
      const outcome = await save.send().catch(
        (): SportsbookPreferenceActionResult<SurfaceWrite> => ({ success: false, error: SAVE_TRANSPORT_ERROR }),
      );
      if (generation !== accountGenerationRef.current) {
        for (const waiter of chain.waiters) waiter({ success: false, error: ACCOUNT_CHANGED_ERROR });
        return;
      }
      if (outcome.success) chain.lastSaved = outcome.data;
      result = outcome.success ? { success: true } : { success: false, error: outcome.error };
    }
    chainsRef.current.delete(surface);
    setPendingWrites((pending) => {
      const rest: Partial<Record<SportsbookSurfaceId, SurfaceWrite>> = { ...pending };
      delete rest[surface];
      return rest;
    });
    const saved = chain.lastSaved;
    if (saved) {
      setState((current) => applySurfaceWrite(current, surface, saved));
      broadcastRef.current?.postMessage(TOOL_OVERRIDE_BROADCAST_MESSAGE);
    }
    void reload("background");
    for (const waiter of chain.waiters) waiter(result);
  }, [reload]);

  const writeToolSelection = useCallback((
    surface: SportsbookSurfaceId,
    build: (profile: GlobalSportsbookProfile) => ToolSave | null,
  ): Promise<ToolSaveResult> => {
    if (
      status !== "ready" ||
      loadedUserIdRef.current !== activeUserIdRef.current
    ) {
      return Promise.resolve({ success: false, error: NOT_READY_ERROR });
    }
    const profile = profileRef.current;
    if (!profile) {
      return Promise.resolve({
        success: false,
        error: "Configure global sportsbook defaults before saving a tool override.",
      });
    }
    const save = build(profile);
    if (!save) return Promise.resolve({ success: false, error: "Invalid sportsbook selection" });
    setPendingWrites((pending) => ({ ...pending, [surface]: save.write }));
    return new Promise((resolve) => {
      const running = chainsRef.current.get(surface);
      if (running) {
        running.next = save;
        running.waiters.push(resolve);
        return;
      }
      const chain: SurfaceChain = { next: save, lastSaved: null, waiters: [resolve] };
      chainsRef.current.set(surface, chain);
      void drainChain(surface, chain, accountGenerationRef.current);
    });
  }, [drainChain, status]);

  const actions = useMemo<SportsbookPreferencesActions>(() => ({
    saveProfile: (input) => withSaving(
      () => saveGlobalSportsbookProfile(input),
      (profile) => setState((current) => ({ ...current, profile })),
    ),
    saveMultiSelection: (surface, selectedBookIds) => writeToolSelection(surface, (profile) => {
      const override = createMultiSportsbookOverride(profile, surface, selectedBookIds);
      return override && {
        write: toSurfaceWrite(override),
        send: async () => mapSaved(await saveMultiSportsbookOverride(surface, selectedBookIds), toSurfaceWrite),
      };
    }),
    saveSingleSelection: (surface, bookId) => writeToolSelection(surface, (profile) => {
      const override = createSingleSportsbookOverride(profile, surface, bookId);
      return override && {
        write: { kind: "put", override },
        send: async () => mapSaved(
          await saveSingleSportsbookOverride(surface, bookId),
          (saved): SurfaceWrite => ({ kind: "put", override: saved }),
        ),
      };
    }),
    clearOverride: (surface) => writeToolSelection(surface, () => ({
      write: DELETE_SURFACE_OVERRIDE,
      send: async () => mapSaved(await clearSportsbookOverride(surface), () => DELETE_SURFACE_OVERRIDE),
    })),
    saveChartBooks: (preference) => withSaving(
      () => saveChartBooksPreference(preference),
      (value) => setState((current) => ({
        ...current,
        chartBooks: { kind: "supported", value },
      })),
    ),
    saveComparisonOrder: (bookIds, hiddenIds) => withSaving(
      () => saveComparisonOrderPreference(bookIds, hiddenIds),
      (value) => setState((current) => ({
        ...current,
        comparisonOrder: { kind: "supported", value },
      })),
    ),
    resetComparisonOrder: () => withSaving(
      () => resetComparisonOrderPreference(),
      () => setState((current) => ({
        ...current,
        comparisonOrder: { kind: "missing" },
      })),
    ),
    retry: () => reload("blocking"),
  }), [reload, withSaving, writeToolSelection]);

  const hasCurrentAccountState = loadedUserIdRef.current === resolvedUserId;
  const currentAccountState = useMemo(
    () => (hasCurrentAccountState ? withPendingWrites(state, pendingWrites) : UNAVAILABLE_STATE),
    [hasCurrentAccountState, pendingWrites, state],
  );
  const currentAccountStatus = hasCurrentAccountState ? status : "loading";
  const currentAccountError = hasCurrentAccountState ? error : null;

  const meta = useMemo<SportsbookPreferencesMeta>(() => ({
    status: currentAccountStatus,
    isSaving,
    error: currentAccountError,
    isConfigured: currentAccountState.profile !== null,
    globalBookIds: getGlobalSportsbookIds(currentAccountState.profile),
    globalDefaultBookId: getGlobalDefaultSportsbookId(currentAccountState.profile),
    getEffectiveBookIds: (surface) =>
      getEffectiveMultiSportsbookIds(currentAccountState.profile, currentAccountState.overrides[surface], surface),
    isOverrideActive: (surface) => {
      const override = currentAccountState.overrides[surface];
      if (
        !currentAccountState.profile ||
        !override ||
        override.epoch !== currentAccountState.profile.overrideEpoch
      ) {
        return false;
      }
      if (override.kind === "multi") return !isEmptyMultiSportsbookOverride(override);
      return getSportsbookSurfacePolicy(surface).supportedBookIds.includes(override.bookId);
    },
  }), [currentAccountError, currentAccountState, currentAccountStatus, isSaving]);

  return (
    <SportsbookPreferencesStateContext.Provider value={currentAccountState}>
      <SportsbookPreferencesActionsContext.Provider value={actions}>
        <SportsbookPreferencesMetaContext.Provider value={meta}>
          {children}
        </SportsbookPreferencesMetaContext.Provider>
      </SportsbookPreferencesActionsContext.Provider>
    </SportsbookPreferencesStateContext.Provider>
  );
}

export function useSportsbookPreferenceState(): SportsbookPreferencesState {
  return useContext(SportsbookPreferencesStateContext) ?? UNAVAILABLE_STATE;
}

export function useSportsbookPreferenceActions(): SportsbookPreferencesActions {
  return useContext(SportsbookPreferencesActionsContext) ?? UNAVAILABLE_ACTIONS;
}

export function useSportsbookPreferenceMeta(): SportsbookPreferencesMeta {
  return useContext(SportsbookPreferencesMetaContext) ?? UNAVAILABLE_META;
}

export function useSportsbookPreferences(): SportsbookPreferencesContextValue {
  return {
    state: useSportsbookPreferenceState(),
    actions: useSportsbookPreferenceActions(),
    meta: useSportsbookPreferenceMeta(),
  };
}
